import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Button,
  Alert,
  TextField,
  Stack,
  Skeleton,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useMaterialReactTable } from "material-react-table";
import DataTable from "../../ui/data/DataTable";
import PageContainer from "../../ui/layout/PageContainer";
import BarChartComponent from "../../components/barChart/BarChartComponent";
import PieChartComponent from "../../components/barChart/PieChartComponent";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Cell,
} from "recharts";
import { getRequestWithToken, patchRequestWithToken } from "../../utils/ApiRequests";
import "./snf.scss";

// CustomTabPanel component for tab content
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`snf-tabpanel-${index}`}
      aria-labelledby={`snf-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `snf-tab-${index}`,
    "aria-controls": `snf-tabpanel-${index}`,
  };
}

const SnfDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(() => {
    const t = parseInt(searchParams.get("tab"), 10);
    return t >= 0 && t <= 4 ? t : 0;
  });
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [abandonedCartStatuses, setAbandonedCartStatuses] = useState({});
  const [apiSummaryData, setApiSummaryData] = useState(null);
  const [summaryError, setSummaryError] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [startDate, setStartDate] = useState("2026-01-01");
  const [endDate, setEndDate] = useState("");

  // Charts state
  const [revenuePeriod, setRevenuePeriod] = useState("weekly");
  const [revenueChartData, setRevenueChartData] = useState([]);
  const [revenueChartLoading, setRevenueChartLoading] = useState(false);
  const [revenueChartError, setRevenueChartError] = useState("");
  const [categoryChartData, setCategoryChartData] = useState([]);
  const [categoryChartLoading, setCategoryChartLoading] = useState(false);
  const [categoryChartError, setCategoryChartError] = useState("");

  // Employers tab state
  const [employersData, setEmployersData] = useState([]);
  const [employersLoading, setEmployersLoading] = useState(false);
  const [employersError, setEmployersError] = useState("");
  const [employersPagination, setEmployersPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [employersPage, setEmployersPage] = useState(1);
  const [employersPageSize, setEmployersPageSize] = useState(20);
  const [employersSearch, setEmployersSearch] = useState("");
  const [employersSearchInput, setEmployersSearchInput] = useState("");
  const [employersSortBy, setEmployersSortBy] = useState("totalSessions");
  const [employersOrder, setEmployersOrder] = useState("desc");
  const [employersStartDate, setEmployersStartDate] = useState("2026-01-01");
  const [employersEndDate, setEmployersEndDate] = useState("");
  const [employersStartDateInput, setEmployersStartDateInput] = useState("2026-01-01");
  const [employersEndDateInput, setEmployersEndDateInput] = useState("");
  const [employersFilterError, setEmployersFilterError] = useState("");

  // City-searches tab state
  const [citySearchesData, setCitySearchesData] = useState([]);
  const [citySearchesLoading, setCitySearchesLoading] = useState(false);
  const [citySearchesError, setCitySearchesError] = useState("");
  const [citySearchesPagination, setCitySearchesPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [citySearchesPage, setCitySearchesPage] = useState(1);
  const [citySearchesPageSize, setCitySearchesPageSize] = useState(20);
  const [citySearchesCityInput, setCitySearchesCityInput] = useState("");
  const [citySearchesStateInput, setCitySearchesStateInput] = useState("");
  const [citySearchesStartDateInput, setCitySearchesStartDateInput] = useState("2026-01-01");
  const [citySearchesEndDateInput, setCitySearchesEndDateInput] = useState("");
  const [citySearchesCityFilter, setCitySearchesCityFilter] = useState("");
  const [citySearchesStateFilter, setCitySearchesStateFilter] = useState("");
  const [citySearchesStartDate, setCitySearchesStartDate] = useState("2026-01-01");
  const [citySearchesEndDate, setCitySearchesEndDate] = useState("");
  const [citySearchesFilterError, setCitySearchesFilterError] = useState("");

  // Purchased data tab state
  const [purchasedData, setPurchasedData] = useState([]);
  const [purchasedLoading, setPurchasedLoading] = useState(false);
  const [purchasedError, setPurchasedError] = useState("");
  const [purchasedPagination, setPurchasedPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [purchasedPage, setPurchasedPage] = useState(1);
  const [purchasedPageSize, setPurchasedPageSize] = useState(20);
  const [purchasedCityInput, setPurchasedCityInput] = useState("");
  const [purchasedStartDateInput, setPurchasedStartDateInput] = useState("2026-01-01");
  const [purchasedEndDateInput, setPurchasedEndDateInput] = useState("");
  const [purchasedCityFilter, setPurchasedCityFilter] = useState("");
  const [purchasedStartDate, setPurchasedStartDate] = useState("2026-01-01");
  const [purchasedEndDate, setPurchasedEndDate] = useState("");
  const [purchasedFilterError, setPurchasedFilterError] = useState("");

  // Abandoned cart tab state
  const [abandonedCartApiData, setAbandonedCartApiData] = useState([]);
  const [abandonedCartLoading, setAbandonedCartLoading] = useState(false);
  const [abandonedCartError, setAbandonedCartError] = useState("");
  const [abandonedCartPagination, setAbandonedCartPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [abandonedCartPage, setAbandonedCartPage] = useState(1);
  const [abandonedCartPageSize, setAbandonedCartPageSize] = useState(20);
  const [abandonedCartStartDateInput, setAbandonedCartStartDateInput] = useState("2026-01-01");
  const [abandonedCartEndDateInput, setAbandonedCartEndDateInput] = useState("");
  const [abandonedCartStartDate, setAbandonedCartStartDate] = useState("2026-01-01");
  const [abandonedCartEndDate, setAbandonedCartEndDate] = useState("");
  const [abandonedCartFilterError, setAbandonedCartFilterError] = useState("");
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  // ============ SAMPLE DATA ============

  // Summary Metrics Data
  const staticSummaryMetrics = useMemo(
    () => [
      {
        label: "Total Employers (Purchased)",
        value: 247,
        trend: "+18%",
        trendUp: true,
        sourceType: "static",
      },
      {
        label: "Total Candidate Data Purchased",
        value: 3842,
        trend: "+24%",
        trendUp: true,
        sourceType: "static",
      },
      {
        label: "Total Searches",
        value: 15678,
        trend: "+31%",
        trendUp: true,
        sourceType: "static",
      },
      {
        label: "Total Abandoned Sessions",
        value: 89,
        trend: "-12%",
        trendUp: false,
        sourceType: "static",
      },
      {
        label: "Revenue (Monthly)",
        value: "₹18.4L",
        trend: "+22%",
        trendUp: true,
        sourceType: "static",
      },
      {
        label: "Revenue (Weekly)",
        value: "₹4.2L",
        trend: "+15%",
        trendUp: true,
        sourceType: "static",
      },
    ],
    []
  );

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchSummaryMetrics = async (fromDate, toDate) => {
    try {
      setSummaryLoading(true);
      const response = await getRequestWithToken(
        `/snf-admin/summary?startDate=${fromDate}&endDate=${toDate}`
      );
      setApiSummaryData(response?.data?.data || null);
      setSummaryError("");
    } catch (error) {
      setSummaryError(error?.response?.data?.msg || "Unable to load summary metrics from API.");
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    const today = formatDate(new Date());
    setEndDate(today);
    setEmployersEndDate(today);
    setEmployersEndDateInput(today);
    setCitySearchesEndDate(today);
    setCitySearchesEndDateInput(today);
    setPurchasedEndDate(today);
    setPurchasedEndDateInput(today);
    setAbandonedCartEndDate(today);
    setAbandonedCartEndDateInput(today);
    fetchSummaryMetrics(startDate, today);
    fetchRevenueChart(startDate, today, "weekly");
    fetchCategoryChart(startDate, today);
  }, []);

  const fetchEmployers = async (params) => {
    try {
      setEmployersLoading(true);
      setEmployersError("");
      const { page, limit, search, sortBy, order, from, to } = params;
      const query = new URLSearchParams();
      query.set("page", page);
      query.set("limit", limit);
      if (search) query.set("search", search);
      if (sortBy) query.set("sortBy", sortBy);
      if (order) query.set("order", order);
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      const response = await getRequestWithToken(`/snf-admin/employers?${query.toString()}`);
      const resData = response?.data;
      setEmployersData(resData?.data || []);
      console.table("Fetched employers data:", resData);
      setEmployersPagination(resData?.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (error) {
      setEmployersError(error?.response?.data?.msg || "Unable to load employers data.");
    } finally {
      setEmployersLoading(false);
    }
  };

  useEffect(() => {
    if (!employersEndDate) return;
    fetchEmployers({
      page: employersPage,
      limit: employersPageSize,
      search: employersSearch,
      sortBy: employersSortBy,
      order: employersOrder,
      from: employersStartDate,
      to: employersEndDate,
    });
  }, [employersPage, employersPageSize, employersSearch, employersSortBy, employersOrder, employersStartDate, employersEndDate]);

  const fetchCitySearches = async (params) => {
    try {
      setCitySearchesLoading(true);
      setCitySearchesError("");
      const { page, limit, city, state, from, to } = params;
      const query = new URLSearchParams();
      query.set("page", page);
      query.set("limit", limit);
      if (city) query.set("city", city);
      if (state) query.set("state", state);
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      const response = await getRequestWithToken(`/snf-admin/city-searches?${query.toString()}`);
      const resData = response?.data;
      setCitySearchesData(resData?.data || []);
      setCitySearchesPagination(resData?.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (error) {
      setCitySearchesError(error?.response?.data?.msg || "Unable to load city searches data.");
    } finally {
      setCitySearchesLoading(false);
    }
  };

  useEffect(() => {
    if (!citySearchesEndDate) return;
    fetchCitySearches({
      page: citySearchesPage,
      limit: citySearchesPageSize,
      city: citySearchesCityFilter,
      state: citySearchesStateFilter,
      from: citySearchesStartDate,
      to: citySearchesEndDate,
    });
  }, [citySearchesPage, citySearchesPageSize, citySearchesCityFilter, citySearchesStateFilter, citySearchesStartDate, citySearchesEndDate]);

  const fetchPurchasedData = async (params) => {
    try {
      setPurchasedLoading(true);
      setPurchasedError("");
      const { page, limit, city, from, to } = params;
      const query = new URLSearchParams();
      query.set("page", page);
      query.set("limit", limit);
      if (city) query.set("city", city);
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      const response = await getRequestWithToken(`/snf-admin/purchases?${query.toString()}`);
      const resData = response?.data;
      setPurchasedData(resData?.data || []);
      setPurchasedPagination(resData?.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (error) {
      setPurchasedError(error?.response?.data?.msg || "Unable to load purchases data.");
    } finally {
      setPurchasedLoading(false);
    }
  };

  useEffect(() => {
    if (!purchasedEndDate) return;
    fetchPurchasedData({
      page: purchasedPage,
      limit: purchasedPageSize,
      city: purchasedCityFilter,
      from: purchasedStartDate,
      to: purchasedEndDate,
    });
  }, [purchasedPage, purchasedPageSize, purchasedCityFilter, purchasedStartDate, purchasedEndDate]);

  const handlePurchasedFilterApply = () => {
    if (!purchasedStartDateInput || !purchasedEndDateInput) {
      setPurchasedFilterError("Please select both start date and end date.");
      return;
    }
    if (new Date(purchasedStartDateInput) > new Date(purchasedEndDateInput)) {
      setPurchasedFilterError("Start date cannot be after end date.");
      return;
    }
    setPurchasedFilterError("");
    setPurchasedPage(1);
    setPurchasedCityFilter(purchasedCityInput);
    setPurchasedStartDate(purchasedStartDateInput);
    setPurchasedEndDate(purchasedEndDateInput);
  };

  const handleCitySearchesFilterApply = () => {
    if (!citySearchesStartDateInput || !citySearchesEndDateInput) {
      setCitySearchesFilterError("Please select both start date and end date.");
      return;
    }
    if (new Date(citySearchesStartDateInput) > new Date(citySearchesEndDateInput)) {
      setCitySearchesFilterError("Start date cannot be after end date.");
      return;
    }
    setCitySearchesFilterError("");
    setCitySearchesPage(1);
    setCitySearchesCityFilter(citySearchesCityInput);
    setCitySearchesStateFilter(citySearchesStateInput);
    setCitySearchesStartDate(citySearchesStartDateInput);
    setCitySearchesEndDate(citySearchesEndDateInput);
  };

  const handleEmployersFilterApply = () => {
    if (!employersStartDateInput || !employersEndDateInput) {
      setEmployersFilterError("Please select both start date and end date.");
      return;
    }
    if (new Date(employersStartDateInput) > new Date(employersEndDateInput)) {
      setEmployersFilterError("Start date cannot be after end date.");
      return;
    }
    setEmployersFilterError("");
    setEmployersPage(1);
    setEmployersSearch(employersSearchInput);
    setEmployersStartDate(employersStartDateInput);
    setEmployersEndDate(employersEndDateInput);
  };

  const fetchAbandonedCarts = async (params) => {
    try {
      setAbandonedCartLoading(true);
      setAbandonedCartError("");
      const { page, limit, from, to } = params;
      const query = new URLSearchParams();
      query.set("page", page);
      query.set("limit", limit);
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      const response = await getRequestWithToken(`/snf-admin/abandoned-carts?${query.toString()}`);
      const resData = response?.data;
      setAbandonedCartApiData(resData?.data || []);
      setAbandonedCartPagination(resData?.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (error) {
      setAbandonedCartError(error?.response?.data?.msg || "Unable to load abandoned cart data.");
    } finally {
      setAbandonedCartLoading(false);
    }
  };

  useEffect(() => {
    if (!abandonedCartEndDate) return;
    fetchAbandonedCarts({
      page: abandonedCartPage,
      limit: abandonedCartPageSize,
      from: abandonedCartStartDate,
      to: abandonedCartEndDate,
    });
  }, [abandonedCartPage, abandonedCartPageSize, abandonedCartStartDate, abandonedCartEndDate]);

  const updateAbandonedCartStatus = async (sessionId, newStatus) => {
    try {
      setStatusUpdateLoading(true);
      await patchRequestWithToken(`/snf-admin/abandoned-carts/${sessionId}/status`, { adminCartStatus: newStatus });
      setAbandonedCartStatuses((prev) => ({ ...prev, [sessionId]: newStatus }));
    } catch (error) {
      setAbandonedCartError(error?.response?.data?.msg || "Failed to update status.");
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleAbandonedCartFilterApply = () => {
    if (!abandonedCartStartDateInput || !abandonedCartEndDateInput) {
      setAbandonedCartFilterError("Please select both start date and end date.");
      return;
    }
    if (new Date(abandonedCartStartDateInput) > new Date(abandonedCartEndDateInput)) {
      setAbandonedCartFilterError("Start date cannot be after end date.");
      return;
    }
    setAbandonedCartFilterError("");
    setAbandonedCartPage(1);
    setAbandonedCartStartDate(abandonedCartStartDateInput);
    setAbandonedCartEndDate(abandonedCartEndDateInput);
  };

  const handleSummaryFilterApply = () => {
    if (!startDate || !endDate) {
      setSummaryError("Please select both start date and end date.");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setSummaryError("Start date cannot be after end date.");
      return;
    }

    fetchSummaryMetrics(startDate, endDate);
    fetchRevenueChart(startDate, endDate, revenuePeriod);
    fetchCategoryChart(startDate, endDate);
  };

  const summaryMetrics = useMemo(() => {
    if (!apiSummaryData) return [];
    return [
      {
        label: "Purchases & Employers",
        items: [
          { label: "Total Purchases", value: apiSummaryData.totalPurchases ?? "-" },
          { label: "Employers Used SNF", value: apiSummaryData.totalEmployersUsedSNF ?? "-" },
        ],
      },
      {
        label: "Candidates & Sessions",
        items: [
          { label: "Candidates Allocated", value: apiSummaryData.totalCandidatesAllocated ?? "-" },
          { label: "Total Sessions", value: apiSummaryData.totalSessions ?? "-" },
        ],
      },
      {
        label: "Abandoned Cart",
        items: [
          { label: "Total Abandoned", value: apiSummaryData.totalAbandonedCarts ?? "-" },
          {
            label: "Cart Rate",
            value: apiSummaryData.abandonedCartRate !== undefined ? `${apiSummaryData.abandonedCartRate}%` : "-",
          },
        ],
      },
      {
        label: "Total Spent",
        items: [
          {
            label: "PHC Spent",
            value: apiSummaryData.totalPhcSpent !== undefined ? Number(apiSummaryData.totalPhcSpent).toLocaleString() : "-",
          },
          {
            label: "Intelligence Fee",
            value: apiSummaryData.totalInrSpent !== undefined ? `₹${Number(apiSummaryData.totalInrSpent).toLocaleString()}` : "-",
          },
        ],
      },
    ];
  }, [apiSummaryData]);

  // Revenue Chart Data
  const fetchRevenueChart = async (fromDate, toDate, period) => {
    try {
      setRevenueChartLoading(true);
      setRevenueChartError("");
      const query = new URLSearchParams();
      if (fromDate) query.set("startDate", fromDate);
      if (toDate) query.set("endDate", toDate);
      if (period) query.set("period", period);
      const response = await getRequestWithToken(`/snf-admin/graphs/weekly-revenue?${query.toString()}`);
      setRevenueChartData(response?.data?.data || []);
    } catch (error) {
      setRevenueChartError(error?.response?.data?.msg || "Unable to load revenue chart.");
    } finally {
      setRevenueChartLoading(false);
    }
  };

  // Category Distribution Data for Pie Chart
  const fetchCategoryChart = async (fromDate, toDate) => {
    try {
      setCategoryChartLoading(true);
      setCategoryChartError("");
      const query = new URLSearchParams();
      if (fromDate) query.set("startDate", fromDate);
      if (toDate) query.set("endDate", toDate);
      const response = await getRequestWithToken(`/snf-admin/graphs/category-distribution?${query.toString()}`);
      setCategoryChartData(response?.data?.data || []);
    } catch (error) {
      setCategoryChartError(error?.response?.data?.msg || "Unable to load category chart.");
    } finally {
      setCategoryChartLoading(false);
    }
  };



  // Abandoned Cart Data (Tab 4)
  // ============ TAB HANDLERS ============

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchParams({ tab: newValue }, { replace: true });
  };

  const handleStatusMenuOpen = (event, row) => {
    setStatusMenuAnchor(event.currentTarget);
    setSelectedRow(row);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
    setSelectedRow(null);
  };

  const handleStatusChange = (newStatus) => {
    if (selectedRow) {
      const sessionId = selectedRow.original.sessionId;
      updateAbandonedCartStatus(sessionId, newStatus);
    }
    handleStatusMenuClose();
  };

  const getRowStatus = (row) => {
    return abandonedCartStatuses[row.sessionId] || row.adminCartStatus || "Pending";
  };

  // ============ EMPLOYERS TABLE (Tab 1) ============

  const employersColumns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "S.No.",
        size: 120,
        enableColumnFilter: false,
        Cell: ({ row, table }) => {
          const pageIndex = table.getState().pagination.pageIndex;
          const pageSize = table.getState().pagination.pageSize;
          const rowIndex = row.index;
          return <span>{rowIndex + 1 + pageIndex * pageSize}</span>;
        },
      },
      {
        accessorKey: "employerName",
        header: "Company",
        size: 160,
        maxSize: 160,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, py: 0.5, maxWidth: 160 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word", whiteSpace: "normal" }}>
              {row.original.employerName || "-"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.contactNumber || "-"}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "city",
        header: "Location",
        size: 160,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
            <Typography variant="body2">{row.original.city || "-"}</Typography>
            <Typography variant="caption" color="text.secondary">{row.original.state || "-"}</Typography>
          </Box>
        ),
      },
      {
        accessorKey: "totalSearches",
        header: "Total Searches",
        size: 130,
      },
      {
        accessorKey: "candidateDemand",
        header: "Candidates",
        size: 150,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
            <Typography variant="body2">Demand: {row.original.candidateDemand ?? "-"}</Typography>
            <Typography variant="caption" color="text.secondary">Provided: {row.original.candidateDataProvided ?? "-"}</Typography>
          </Box>
        ),
      },
      {
        accessorKey: "urgencyLevel",
        header: "Urgency / Category",
        size: 190,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
            <Typography variant="body2">{row.original.urgencyLevel || "-"}</Typography>
            <Typography variant="caption" color="text.secondary">{row.original.mostSearchedCategory || "-"}</Typography>
          </Box>
        ),
      },
      {
        accessorKey: "lastSessionAt",
        header: "Last Session",
        size: 160,
        Cell: ({ cell }) => {
          const val = cell.getValue();
          if (!val) return "-";
          return new Date(val).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 100,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <IconButton
            color="primary"
            onClick={() => window.open(`/employer/detail/${row.original.employerId}`, "_blank", "noopener,noreferrer")}
            title="View Details"
          >
            <VisibilityIcon />
          </IconButton>
        ),
      },
    ],
    [navigate]
  );

  const employersTable = useMaterialReactTable({
    columns: employersColumns,
    data: employersData,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    layoutMode: "grid",
    state: { isLoading: employersLoading },
    initialState: { density: "compact" },
    muiTableBodyCellProps: { sx: { borderBottom: "1px solid rgba(0,0,0,0.07)" } },
    muiTableContainerProps: { sx: { maxHeight: "520px" } },
  });

  // ============ MOST SEARCHES TABLE (Tab 2) ============

  const mostSearchesColumns = useMemo(
    () => [
      {
        accessorKey: "rank",
        header: "Rank",
        size: 70,
      },
      {
        accessorKey: "category",
        header: "Category / Role",
        size: 200,
      },
      {
        accessorKey: "city",
        header: "City",
        size: 140,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "searchCount",
        header: "Search Count",
        size: 140,
      },
    ],
    []
  );

  const mostSearchesTable = useMaterialReactTable({
    columns: mostSearchesColumns,
    data: citySearchesData,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    layoutMode: "grid",
    state: { isLoading: citySearchesLoading },
    initialState: { density: "compact" },
    muiTableBodyCellProps: { sx: { borderBottom: "1px solid rgba(0,0,0,0.07)" } },
    muiTableContainerProps: { sx: { maxHeight: "520px" } },
  });

  // ============ PURCHASED DATA TABLE (Tab 3) ============

  const purchasedDataColumns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "S.No.",
        size: 120,
        enableColumnFilter: false,
        Cell: ({ row, table }) => {
          const pageIndex = table.getState().pagination.pageIndex;
          const pageSize = table.getState().pagination.pageSize;
          const rowIndex = row.index;
          return <span>{rowIndex + 1 + pageIndex * pageSize}</span>;
        },
      },
      {
        accessorKey: "employerName",
        header: "Employer",
        size: 220,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, py: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
              {row.original.employerName || "-"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.contactNumber || "-"}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "city",
        header: "City",
        size: 120,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "candidateDemand",
        header: "Demand / Provided",
        size: 140,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
            <Typography variant="body2">Demand: {row.original.candidateDemand ?? "-"}</Typography>
            <Typography variant="caption" color="text.secondary">Provided: {row.original.dataProvided ?? "-"}</Typography>
          </Box>
        ),
      },
      {
        accessorKey: "costOfPurchase",
        header: "Cost (PHC)",
        size: 110,
        Cell: ({ cell }) => Number(cell.getValue()).toLocaleString(),
      },
      {
        accessorKey: "purchaseDate",
        header: "Purchase Date",
        size: 160,
        Cell: ({ cell }) => {
          const val = cell.getValue();
          if (!val) return "-";
          return new Date(val).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        },
      },
      {
        accessorKey: "demandFulfilled",
        header: "Fulfillment",
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "success.main" }}>
              <TrendingUpIcon fontSize="small" />
              <Typography variant="body2">Fulfilled</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "error.main" }}>
              <TrendingDownIcon fontSize="small" />
              <Typography variant="body2">Partial</Typography>
            </Box>
          );
        },
      },
    ],
    []
  );

  const purchasedDataTable = useMaterialReactTable({
    columns: purchasedDataColumns,
    data: purchasedData,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    layoutMode: "grid",
    state: { isLoading: purchasedLoading },
    initialState: { density: "compact" },
    muiTableBodyCellProps: { sx: { borderBottom: "1px solid rgba(0,0,0,0.07)" } },
    muiTableContainerProps: { sx: { maxHeight: "520px" } },
  });

  // ============ ABANDONED CART TABLE (Tab 4) ============

  const abandonedCartColumns = useMemo(
    () => [

      {
        accessorKey: "id",
        header: "S.No.",
        size: 120,
        enableColumnFilter: false,
        Cell: ({ row, table }) => {
          const pageIndex = table.getState().pagination.pageIndex;
          const pageSize = table.getState().pagination.pageSize;
          const rowIndex = row.index;
          return <span>{rowIndex + 1 + pageIndex * pageSize}</span>;
        },
      },
      {
        accessorKey: "employerName",
        header: "Employer",
        size: 260,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, py: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
              {row.original.employerName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.employerPhone}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
              {row.original.employerEmail}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "numberOfSessions",
        header: "Sessions",
        size: 100,
      },
      {
        accessorKey: "dateTime",
        header: "Date & Time",
        size: 180,
        Cell: ({ cell }) => {
          const val = cell.getValue();
          if (!val) return "-";
          return new Date(val).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
      },
      {
        accessorKey: "cartValue",
        header: "Cart Value (PHC)",
        size: 130,
        Cell: ({ cell }) => Number(cell.getValue()).toLocaleString(),
      },
      {
        accessorKey: "searchesCity",
        header: "City",
        size: 120,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "adminCartStatus",
        header: "Status",
        size: 150,
        Cell: ({ row }) => {
          const value = getRowStatus(row.original);
          const styleMap = {
            "Pending": { backgroundColor: "rgba(255,152,0,0.12)", color: "#bf6000" },
            "Contacted": { backgroundColor: "rgba(33,150,243,0.12)", color: "#0a5c99" },
            "Follow-up Required": { backgroundColor: "rgba(244,67,54,0.12)", color: "#b71c1c" },
            "Converted": { backgroundColor: "rgba(76,175,80,0.12)", color: "#2e7d32" },
          };
          return (
            <Chip
              label={value}
              size="small"
              sx={styleMap[value] || { backgroundColor: "rgba(0,0,0,0.06)", color: "text.secondary" }}
            />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            disabled={statusUpdateLoading}
            onClick={(e) => handleStatusMenuOpen(e, row)}
          >
            Update
          </Button>
        ),
      },
    ],
    [abandonedCartStatuses, getRowStatus, handleStatusMenuOpen, statusUpdateLoading]
  );

  const abandonedCartTable = useMaterialReactTable({
    columns: abandonedCartColumns,
    data: abandonedCartApiData,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    layoutMode: "grid",
    state: { isLoading: abandonedCartLoading },
    initialState: { density: "compact" },
    muiTableBodyCellProps: { sx: { borderBottom: "1px solid rgba(0,0,0,0.07)" } },
    muiTableContainerProps: { sx: { maxHeight: "520px" } },
  });

  // ============ RENDER ============

  return (
    <PageContainer
      title="SNF Dashboard"
      subtitle="Smart Nearby Feature - Monitor candidate purchase data for employers and recruiters"
    >
      <div className="snf-dashboard">
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="SNF Dashboard Tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Dashboard / Metrics" {...a11yProps(0)} />
            <Tab label="Employers" {...a11yProps(1)} />
            <Tab label="Most Searches (City-wise)" {...a11yProps(2)} />
            <Tab label="Purchased Data" {...a11yProps(3)} />
            <Tab label="Abandoned Cart" {...a11yProps(4)} />
          </Tabs>
        </Box>

        {/* TAB 0: Dashboard / Metrics */}
        <CustomTabPanel value={tabValue} index={0}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            sx={{ mb: 2 }}
          >
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              onClick={handleSummaryFilterApply}
              disabled={summaryLoading}
            >
              {summaryLoading ? "Applying..." : "Apply"}
            </Button>
          </Stack>

          {summaryError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {summaryError}
            </Alert>
          ) : null}
          <Grid container spacing={2}>
            {summaryLoading
              ? Array.from({ length: 4 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card sx={{ boxShadow: "var(--jy-card-shadow)", border: "1px solid var(--jy-border)" }}>
                    <CardContent>
                      <Skeleton variant="text" width="50%" height={18} sx={{ mb: 1.5 }} />
                      <Grid container spacing={1}>
                        {[0, 1].map((i) => (
                          <Grid item xs={6} key={i}>
                            <Box sx={{ p: 1, borderRadius: 1, backgroundColor: "rgba(0,0,0,0.04)" }}>
                              <Skeleton variant="text" width="80%" height={14} />
                              <Skeleton variant="text" width="60%" height={32} sx={{ mt: 0.5 }} />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))
              : summaryMetrics.map((metric, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    className="snf-metric-card"
                    sx={{
                      boxShadow: "var(--jy-card-shadow)",
                      border: "1px solid var(--jy-border)",
                      backgroundColor: "#fff",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                          color: "primary.main",
                          display: "block",
                          mb: 1.5,
                        }}
                      >
                        {metric.label}
                      </Typography>
                      <Grid container spacing={1}>
                        {metric.items.map((item, i) => (
                          <Grid item xs={6} key={i}>
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                backgroundColor: "rgba(25, 118, 210, 0.04)",
                                border: "1px solid rgba(25, 118, 210, 0.08)",
                              }}
                            >
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.3 }}>
                                {item.label}
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, lineHeight: 1.2 }}>
                                {typeof item.value === "number" ? item.value.toLocaleString() : item.value}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={7}>
              <Card sx={{ boxShadow: "var(--jy-card-shadow)", border: "1px solid var(--jy-border)" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Revenue Trend</Typography>
                      <Typography variant="caption" color="text.secondary">PHC Spent (bars) &amp; Purchase Count (line)</Typography>
                    </Box>
                    {/* <Stack direction="row" spacing={0.5}>
                      <Button
                        size="small"
                        variant={revenuePeriod === "weekly" ? "contained" : "outlined"}
                        onClick={() => {
                          setRevenuePeriod("weekly");
                          fetchRevenueChart(startDate, endDate, "weekly");
                        }}
                        disabled={revenueChartLoading}
                      >
                        Weekly
                      </Button>
                      <Button
                        size="small"
                        variant={revenuePeriod === "monthly" ? "contained" : "outlined"}
                        onClick={() => {
                          setRevenuePeriod("monthly");
                          fetchRevenueChart(startDate, endDate, "monthly");
                        }}
                        disabled={revenueChartLoading}
                      >
                        Monthly
                      </Button>
                    </Stack> */}
                  </Box>
                  {revenueChartError ? (
                    <Alert severity="warning" sx={{ mb: 1 }}>{revenueChartError}</Alert>
                  ) : null}
                  {revenueChartLoading ? (
                    <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 1 }} />
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <ComposedChart data={revenueChartData} margin={{ top: 10, right: 40, left: 0, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                        <YAxis
                          yAxisId="left"
                          orientation="left"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                          label={{ value: "PHC", angle: -90, position: "insideLeft", offset: 10, fontSize: 11 }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tick={{ fontSize: 11 }}
                          label={{ value: "Purchases", angle: 90, position: "insideRight", offset: 10, fontSize: 11 }}
                        />
                        <RechartsTooltip
                          formatter={(value, name) =>
                            name === "Revenue (PHC)"
                              ? [`${Number(value).toLocaleString()} PHC`, name]
                              : [value, name]
                          }
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar yAxisId="left" dataKey="count" name="Revenue (PHC)" fill="#1976d2" radius={[3, 3, 0, 0]} />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="purchaseCount"
                          name="Purchases"
                          stroke="#ff9800"
                          strokeWidth={2}
                          dot={{ r: 4, fill: "#ff9800" }}
                          activeDot={{ r: 6 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card sx={{ boxShadow: "var(--jy-card-shadow)", border: "1px solid var(--jy-border)" }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Roles Distribution</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Candidate data by roles</Typography>
                  {categoryChartError ? (
                    <Alert severity="warning" sx={{ mb: 1 }}>{categoryChartError}</Alert>
                  ) : null}
                  {categoryChartLoading ? (
                    <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 1 }} />
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(200, categoryChartData.length * 44)}>
                      <BarChart
                        data={categoryChartData}
                        layout="vertical"
                        margin={{ top: 5, right: 60, left: 8, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
                        <YAxis
                          dataKey="label"
                          type="category"
                          width={150}
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => v.length > 22 ? `${v.slice(0, 22)}…` : v}
                        />
                        <RechartsTooltip
                          formatter={(value, name, props) => [
                            `${Number(value).toLocaleString()} candidates`,
                            props.payload.label,
                          ]}
                        />
                        <Bar dataKey="value" name="Candidates" radius={[0, 3, 3, 0]} label={{ position: "right", fontSize: 11, formatter: (v) => Number(v).toLocaleString() }}>
                          {categoryChartData.map((_, i) => (
                            <Cell key={i} fill={["#1976d2", "#42a5f5", "#66bb6a", "#ff9800", "#ef5350", "#ab47bc", "#26c6da"][i % 7]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CustomTabPanel>

        {/* TAB 1: Employers Table */}
        <CustomTabPanel value={tabValue} index={1}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <TextField
              label="Search Company"
              size="small"
              placeholder="e.g. Acme"
              value={employersSearchInput}
              onChange={(e) => setEmployersSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleEmployersFilterApply(); }}
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={employersStartDateInput}
              onChange={(e) => setEmployersStartDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={employersEndDateInput}
              onChange={(e) => setEmployersEndDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Sort By"
              size="small"
              select
              value={employersSortBy}
              onChange={(e) => { setEmployersSortBy(e.target.value); setEmployersPage(1); }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="totalSessions">Total Sessions</MenuItem>
              <MenuItem value="totalPurchases">Total Purchases</MenuItem>
              <MenuItem value="abandonedSessions">Abandoned Sessions</MenuItem>
              <MenuItem value="lastSessionAt">Last Session</MenuItem>
            </TextField>
            <TextField
              label="Order"
              size="small"
              select
              value={employersOrder}
              onChange={(e) => { setEmployersOrder(e.target.value); setEmployersPage(1); }}
              sx={{ minWidth: 100 }}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="desc">Desc</MenuItem>
              <MenuItem value="asc">Asc</MenuItem>
            </TextField>
            <Button
              variant="contained"
              onClick={handleEmployersFilterApply}
              disabled={employersLoading}
            >
              {employersLoading ? "Loading..." : "Apply"}
            </Button>
          </Stack>

          {employersFilterError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>{employersFilterError}</Alert>
          ) : null}
          {employersError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{employersError}</Alert>
          ) : null}

          <DataTable table={employersTable} />

          {/* Pagination controls */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {employersPagination.total > 0
                ? `Showing page ${employersPagination.page} of ${employersPagination.pages} (${employersPagination.total} total)`
                : "No results"}
            </Typography>
            <TextField
              label="Rows"
              size="small"
              select
              value={employersPageSize}
              onChange={(e) => { setEmployersPageSize(Number(e.target.value)); setEmployersPage(1); }}
              sx={{ width: 80 }}
            >
              {[10, 20, 50].map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              variant="outlined"
              disabled={employersPage <= 1 || employersLoading}
              onClick={() => setEmployersPage((p) => p - 1)}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={employersPage >= employersPagination.pages || employersLoading}
              onClick={() => setEmployersPage((p) => p + 1)}
            >
              Next
            </Button>
          </Stack>
        </CustomTabPanel>

        {/* TAB 2: Most Searches (City-wise) */}
        <CustomTabPanel value={tabValue} index={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <TextField
              label="City"
              size="small"
              placeholder="e.g. Mumbai"
              value={citySearchesCityInput}
              onChange={(e) => setCitySearchesCityInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleCitySearchesFilterApply(); }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              label="State"
              size="small"
              placeholder="e.g. Maharashtra"
              value={citySearchesStateInput}
              onChange={(e) => setCitySearchesStateInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleCitySearchesFilterApply(); }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={citySearchesStartDateInput}
              onChange={(e) => setCitySearchesStartDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={citySearchesEndDateInput}
              onChange={(e) => setCitySearchesEndDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              onClick={handleCitySearchesFilterApply}
              disabled={citySearchesLoading}
            >
              {citySearchesLoading ? "Loading..." : "Apply"}
            </Button>
          </Stack>

          {citySearchesFilterError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>{citySearchesFilterError}</Alert>
          ) : null}
          {citySearchesError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{citySearchesError}</Alert>
          ) : null}

          <DataTable table={mostSearchesTable} />

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {citySearchesPagination.total > 0
                ? `Showing page ${citySearchesPagination.page} of ${citySearchesPagination.pages} (${citySearchesPagination.total} total)`
                : "No results"}
            </Typography>
            <TextField
              label="Rows"
              size="small"
              select
              value={citySearchesPageSize}
              onChange={(e) => { setCitySearchesPageSize(Number(e.target.value)); setCitySearchesPage(1); }}
              sx={{ width: 80 }}
            >
              {[10, 20, 50].map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              variant="outlined"
              disabled={citySearchesPage <= 1 || citySearchesLoading}
              onClick={() => setCitySearchesPage((p) => p - 1)}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={citySearchesPage >= citySearchesPagination.pages || citySearchesLoading}
              onClick={() => setCitySearchesPage((p) => p + 1)}
            >
              Next
            </Button>
          </Stack>
        </CustomTabPanel>

        {/* TAB 3: Purchased Data */}
        <CustomTabPanel value={tabValue} index={3}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap="wrap"
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            sx={{ mb: 2 }}
          >
            {/* <TextField
              label="City"
              size="small"
              placeholder="e.g. Mumbai"
              value={purchasedCityInput}
              onChange={(e) => setPurchasedCityInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handlePurchasedFilterApply(); }}
              sx={{ minWidth: 150 }}
            /> */}
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={purchasedStartDateInput}
              onChange={(e) => setPurchasedStartDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={purchasedEndDateInput}
              onChange={(e) => setPurchasedEndDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              onClick={handlePurchasedFilterApply}
              disabled={purchasedLoading}
            >
              {purchasedLoading ? "Loading..." : "Apply"}
            </Button>
          </Stack>

          {purchasedFilterError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>{purchasedFilterError}</Alert>
          ) : null}
          {purchasedError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{purchasedError}</Alert>
          ) : null}

          <DataTable table={purchasedDataTable} />

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {purchasedPagination.total > 0
                ? `Showing page ${purchasedPagination.page} of ${purchasedPagination.pages} (${purchasedPagination.total} total)`
                : "No results"}
            </Typography>
            <TextField
              label="Rows"
              size="small"
              select
              value={purchasedPageSize}
              onChange={(e) => { setPurchasedPageSize(Number(e.target.value)); setPurchasedPage(1); }}
              sx={{ width: 80 }}
            >
              {[10, 20, 50].map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              variant="outlined"
              disabled={purchasedPage <= 1 || purchasedLoading}
              onClick={() => setPurchasedPage((p) => p - 1)}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={purchasedPage >= purchasedPagination.pages || purchasedLoading}
              onClick={() => setPurchasedPage((p) => p + 1)}
            >
              Next
            </Button>
          </Stack>
        </CustomTabPanel>

        {/* TAB 4: Abandoned Cart */}
        <CustomTabPanel value={tabValue} index={4}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap="wrap"
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            sx={{ mb: 2 }}
          >
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={abandonedCartStartDateInput}
              onChange={(e) => setAbandonedCartStartDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={abandonedCartEndDateInput}
              onChange={(e) => setAbandonedCartEndDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              onClick={handleAbandonedCartFilterApply}
              disabled={abandonedCartLoading}
            >
              {abandonedCartLoading ? "Loading..." : "Apply"}
            </Button>
          </Stack>

          {abandonedCartFilterError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>{abandonedCartFilterError}</Alert>
          ) : null}
          {abandonedCartError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{abandonedCartError}</Alert>
          ) : null}

          <DataTable table={abandonedCartTable} />

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {abandonedCartPagination.total > 0
                ? `Showing page ${abandonedCartPagination.page} of ${abandonedCartPagination.pages} (${abandonedCartPagination.total} total)`
                : "No results"}
            </Typography>
            <TextField
              label="Rows"
              size="small"
              select
              value={abandonedCartPageSize}
              onChange={(e) => { setAbandonedCartPageSize(Number(e.target.value)); setAbandonedCartPage(1); }}
              sx={{ width: 80 }}
            >
              {[10, 20, 50].map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              variant="outlined"
              disabled={abandonedCartPage <= 1 || abandonedCartLoading}
              onClick={() => setAbandonedCartPage((p) => p - 1)}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={abandonedCartPage >= abandonedCartPagination.pages || abandonedCartLoading}
              onClick={() => setAbandonedCartPage((p) => p + 1)}
            >
              Next
            </Button>
          </Stack>

          {/* Status Update Menu */}
          <Menu
            anchorEl={statusMenuAnchor}
            open={Boolean(statusMenuAnchor)}
            onClose={handleStatusMenuClose}
          >
            <MenuItem onClick={() => handleStatusChange("Pending")}>Pending</MenuItem>
            <MenuItem onClick={() => handleStatusChange("Contacted")}>Contacted</MenuItem>
            <MenuItem onClick={() => handleStatusChange("Follow-up Required")}>Follow-up Required</MenuItem>
            <MenuItem onClick={() => handleStatusChange("Converted")}>Converted</MenuItem>
          </Menu>
        </CustomTabPanel>
      </div>
    </PageContainer>
  );
};

export default SnfDashboard;
